import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  Button,
  Text,
  Input,
  Textarea,
  makeStyles,
  shorthands,
  tokens,
  Divider,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Edit24Regular,
  Link24Regular,
  Comment24Regular,
  Checkmark24Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '1200px',
    width: '90vw',
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 768px)': {
      maxWidth: '100vw',
      width: '100vw',
      height: '100vh',
      ...shorthands.borderRadius('0'),
    },
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.overflow('hidden'),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  content: {
    display: 'flex',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      ...shorthands.overflow('auto'),
    },
  },
  contentWithoutSidebar: {
    flex: 1,
  },
  contentWithSidebar: {
    flex: 1,
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  mainContentScroll: {
    flex: 1,
    ...shorthands.padding('24px'),
    overflowY: 'auto',
  },
  sidebar: {
    width: '380px',
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke1),
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 768px)': {
      width: '100%',
      ...shorthands.borderLeft('0'),
      ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke1),
    },
  },
  sidebarHeader: {
    ...shorthands.padding('16px', '20px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sidebarContent: {
    ...shorthands.padding('20px'),
    flexGrow: 1,
    overflowY: 'auto',
  },
  commentInput: {
    width: '100%',
    minHeight: '80px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  fieldGroup: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  fieldLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
  },
  fieldValue: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  fieldValueClickable: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    ...shorthands.padding('6px', '8px'),
    ...shorthands.borderRadius('4px'),
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  editButton: {
    marginTop: '4px',
  },
  editActions: {
    display: 'flex',
    gap: '8px',
    ...shorthands.padding('16px', '24px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke1),
    justifyContent: 'flex-end',
  },
  savedIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: tokens.colorPaletteGreenForeground1,
    marginTop: '4px',
  },
});

interface Job {
  id: string;
  orderId: string;
  customer: string;
  type: 'Delivery' | 'Pickup' | 'Exchange';
  dumpsterSize: string;
  address: string;
  time: string;
  status: 'Deliver' | 'Pickup' | 'Service' | 'Swapout' | 'On Site';
  // Location data
  latitude?: number;
  longitude?: number;
  // Extended details
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  rentalStartDate?: string;
  rentalEndDate?: string;
  rentalDuration?: string;
  price?: string;
  depositAmount?: string;
  equipmentType?: string;
  equipmentId?: string;
  specialInstructions?: string;
  accessNotes?: string;
  gateCode?: string;
  permitRequired?: boolean;
  permitNumber?: string;
  booqableBookingId?: string;
  createdDate?: string;
  lastModifiedDate?: string;
}

interface JobDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job | null;
}

export default function JobDetailsModal({ isOpen, onClose, job }: JobDetailsModalProps) {
  const styles = useStyles();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);

  const [editingFields, setEditingFields] = useState<Set<keyof Job>>(new Set());
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [comment, setComment] = useState('');
  const [isEditAllMode, setIsEditAllMode] = useState(false);
  const [savedField, setSavedField] = useState<keyof Job | null>(null);
  const [fieldOriginalValues, setFieldOriginalValues] = useState<Partial<Job>>({});

  // Form state for editing - initialize with job data whenever job changes
  const [editedJob, setEditedJob] = useState<Job>(job || {} as Job);
  const [hasChanges, setHasChanges] = useState(false);

  // Update editedJob when job prop changes
  if (job && editedJob.id !== job.id) {
    setEditedJob({ ...job });
    setIsEditAllMode(false);
    setHasChanges(false);
  }

  if (!job) return null;

  const handleCopyLink = () => {
    const link = `${window.location.origin}/loadboard?job=${job.id}`;
    navigator.clipboard.writeText(link);

    dispatchToast(
      <Toast>
        <ToastTitle>Link copied to clipboard</ToastTitle>
      </Toast>,
      { intent: 'success' }
    );
  };

  const handleEditAll = () => {
    const allFields = new Set<keyof Job>(['orderId', 'type', 'dumpsterSize', 'status', 'time', 'customer', 'address']);
    setEditingFields(allFields);
    setIsEditAllMode(true);
  };

  const handleFieldClick = (field: keyof Job) => {
    // Only allow individual editing if not in edit all mode
    if (!isEditAllMode) {
      setEditingFields(prev => new Set(prev).add(field));
      setSavedField(null); // Clear any saved indicator
      // Store the original value when entering edit mode
      setFieldOriginalValues(prev => ({ ...prev, [field]: editedJob[field] }));
    }
  };

  const handleFieldBlur = (field: keyof Job) => {
    // In individual edit mode, save the value and exit edit mode
    if (!isEditAllMode) {
      const originalValue = fieldOriginalValues[field];
      const currentValue = editedJob[field];
      const hasChanged = originalValue !== currentValue;

      setEditingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(field);
        return newSet;
      });

      // Only show saved indicator and save if value actually changed
      if (hasChanged) {
        // Auto-save the individual field change
        // Save logic would go here (e.g., API call to save this specific field)

        // Show saved indicator
        setSavedField(field);

        // Clear saved indicator after 2 seconds
        setTimeout(() => {
          setSavedField(null);
        }, 2000);
      }

      // Clear the stored original value
      setFieldOriginalValues(prev => {
        const newVals = { ...prev };
        delete newVals[field];
        return newVals;
      });

      // Reset hasChanges since we auto-saved
      setHasChanges(false);
    }
    // In edit all mode, do nothing on blur - fields stay editable
  };

  const handleCancelEdit = () => {
    // Reset to original job values and close modal
    setEditedJob({ ...job });
    setEditingFields(new Set());
    setHasChanges(false);
    setIsEditAllMode(false);
    onClose();
  };

  const handleSaveEdit = () => {
    // Save all changes in edit all mode
    // Save logic would go here (e.g., API call to save entire job)

    setEditingFields(new Set());
    setHasChanges(false);
    setIsEditAllMode(false);

    dispatchToast(
      <Toast>
        <ToastTitle>Changes saved successfully</ToastTitle>
      </Toast>,
      { intent: 'success' }
    );
  };

  const handleToggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const updateEditedField = (field: keyof Job, value: string) => {
    setEditedJob({ ...editedJob, [field]: value });
    setHasChanges(true);
  };

  const isFieldEditing = (field: keyof Job) => editingFields.has(field);

  const renderField = (field: keyof Job, label: string, value: string, type: 'input' | 'textarea' = 'input', showLabel: boolean = true) => {
    const isEditing = isFieldEditing(field);
    const showSaved = savedField === field;

    return (
      <div className={styles.field}>
        {showLabel && <Text className={styles.fieldLabel}>{label}</Text>}
        {isEditAllMode ? (
          type === 'textarea' ? (
            <Textarea
              value={(editedJob[field] as string) || ''}
              onChange={(e, data) => updateEditedField(field, data.value)}
              style={{ minHeight: '80px' }}
            />
          ) : (
            <Input
              value={(editedJob[field] as string) || ''}
              onChange={(e, data) => updateEditedField(field, data.value)}
            />
          )
        ) : isEditing ? (
          type === 'textarea' ? (
            <Textarea
              value={(editedJob[field] as string) || ''}
              onChange={(e, data) => updateEditedField(field, data.value)}
              onBlur={() => handleFieldBlur(field)}
              autoFocus
              style={{ minHeight: '80px' }}
            />
          ) : (
            <Input
              value={(editedJob[field] as string) || ''}
              onChange={(e, data) => updateEditedField(field, data.value)}
              onBlur={() => handleFieldBlur(field)}
              autoFocus
            />
          )
        ) : (
          <Text
            className={styles.fieldValueClickable}
            onClick={() => handleFieldClick(field)}
          >
            {value || 'N/A'}
          </Text>
        )}
        {showSaved && !isEditAllMode && (
          <div className={styles.savedIndicator}>
            <Checkmark24Regular style={{ fontSize: '14px' }} />
            <Text>Saved</Text>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(event, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          {/* Header */}
          <div className={styles.header}>
            <div>
              <Text style={{ fontSize: '20px', fontWeight: '600' }}>{job.orderId}</Text>
              <Text style={{ fontSize: '14px', color: tokens.colorNeutralForeground3, display: 'block' }}>
                {job.customer}
              </Text>
            </div>
            <div className={styles.headerActions}>
              <Button
                appearance="subtle"
                icon={<Edit24Regular />}
                onClick={handleEditAll}
              >
                Edit all
              </Button>
              <Button
                appearance="subtle"
                icon={<Link24Regular />}
                onClick={handleCopyLink}
              />
              <Button
                appearance="subtle"
                icon={<Comment24Regular />}
                onClick={handleToggleSidebar}
              />
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            </div>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {/* Main Content */}
            <div className={styles.mainContent}>
              <div className={styles.mainContentScroll}>
                {/* Job Details Section */}
                <div className={styles.section}>
                  <Text className={styles.sectionTitle}>Job Details</Text>
                  <div className={styles.fieldGroup}>
                    {renderField('orderId', 'Order ID', job.orderId)}
                    {renderField('booqableBookingId', 'Booqable Booking ID', job.booqableBookingId || '')}
                    {renderField('type', 'Type', job.type)}
                    {renderField('status', 'Status', job.status)}
                    {renderField('time', 'Scheduled Time', job.time)}
                    {renderField('createdDate', 'Created Date', job.createdDate || '')}
                  </div>
                </div>

                <Divider style={{ marginBottom: '24px' }} />

                {/* Equipment Information Section */}
                <div className={styles.section}>
                  <Text className={styles.sectionTitle}>Equipment Information</Text>
                  <div className={styles.fieldGroup}>
                    {renderField('dumpsterSize', 'Dumpster Size', job.dumpsterSize)}
                    {renderField('equipmentType', 'Equipment Type', job.equipmentType || '')}
                    {renderField('equipmentId', 'Equipment ID', job.equipmentId || '')}
                  </div>
                </div>

                <Divider style={{ marginBottom: '24px' }} />

                {/* Customer Information Section */}
                <div className={styles.section}>
                  <Text className={styles.sectionTitle}>Customer Information</Text>
                  <div className={styles.fieldGroup}>
                    {renderField('customer', 'Business Name', job.customer)}
                    {renderField('contactName', 'Contact Name', job.contactName || '')}
                    {renderField('contactPhone', 'Phone', job.contactPhone || '')}
                    {renderField('contactEmail', 'Email', job.contactEmail || '')}
                    <div style={{ gridColumn: '1 / -1' }}>
                      {renderField('address', 'Address', job.address)}
                    </div>
                  </div>
                </div>

                <Divider style={{ marginBottom: '24px' }} />

                {/* Location Section */}
                {job.latitude && job.longitude && (
                  <>
                    <div className={styles.section}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <Text className={styles.sectionTitle}>Location</Text>
                      </div>
                      <div style={{ marginTop: '16px', width: '100%', height: '300px', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                        <iframe
                          title='jobLocationMap'
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          style={{ border: 0 }}
                          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${job.latitude},${job.longitude}&zoom=15&maptype=roadmap`}
                          allowFullScreen
                        />
                      </div>
                    </div>
                    <Divider style={{ marginBottom: '24px' }} />
                  </>
                )}

                {/* Rental Information Section */}
                <div className={styles.section}>
                  <Text className={styles.sectionTitle}>Rental Information</Text>
                  <div className={styles.fieldGroup}>
                    {renderField('rentalStartDate', 'Start Date', job.rentalStartDate || '')}
                    {renderField('rentalEndDate', 'End Date', job.rentalEndDate || '')}
                    {renderField('rentalDuration', 'Duration', job.rentalDuration || '')}
                    {renderField('price', 'Price', job.price || '')}
                    {renderField('depositAmount', 'Deposit Amount', job.depositAmount || '')}
                  </div>
                </div>

                <Divider style={{ marginBottom: '24px' }} />

                {/* Permit Information Section */}
                {(job.permitRequired || isEditAllMode) && (
                  <>
                    <div className={styles.section}>
                      <Text className={styles.sectionTitle}>Permit Information</Text>
                      <div className={styles.fieldGroup}>
                        <div className={styles.field}>
                          <Text className={styles.fieldLabel}>Permit Required</Text>
                          <Text className={styles.fieldValue}>
                            {job.permitRequired ? 'Yes' : 'No'}
                          </Text>
                        </div>
                        {renderField('permitNumber', 'Permit Number', job.permitNumber || '')}
                      </div>
                    </div>
                    <Divider style={{ marginBottom: '24px' }} />
                  </>
                )}

                {/* Site Access Information Section */}
                <div className={styles.section}>
                  <Text className={styles.sectionTitle}>Site Access Information</Text>
                  <div className={styles.fieldGroup}>
                    {renderField('gateCode', 'Gate Code', job.gateCode || '')}
                    <div style={{ gridColumn: '1 / -1' }}>
                      {renderField('accessNotes', 'Access Notes', job.accessNotes || 'No access notes provided.', 'textarea')}
                    </div>
                  </div>
                </div>

                <Divider style={{ marginBottom: '24px' }} />

                {/* Special Instructions Section */}
                <div className={styles.section}>
                  <Text className={styles.sectionTitle}>Special Instructions</Text>
                  {renderField('specialInstructions', 'Special Instructions', job.specialInstructions || 'No special instructions provided.', 'textarea')}
                </div>
              </div>

              {/* Edit Actions - Only visible in edit all mode */}
              {isEditAllMode && (
                <div className={styles.editActions}>
                  <Button appearance="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button appearance="primary" onClick={handleSaveEdit} disabled={!hasChanges}>
                    Save
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar - Comments */}
            {isSidebarVisible && (
              <div className={styles.sidebar}>
                <div className={styles.sidebarHeader}>
                  <Text style={{ fontSize: '16px', fontWeight: '600' }}>Comments</Text>
                </div>
                <div className={styles.sidebarContent}>
                  <Textarea
                    className={styles.commentInput}
                    placeholder="@mention or comment"
                    value={comment}
                    onChange={(e, data) => setComment(data.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </DialogBody>
      </DialogSurface>
      <Toaster toasterId={toasterId} />
    </Dialog>
  );
}
